import React from 'react';
import { Button } from 'react-bootstrap';
import './AllowanceTableRow.scss';

interface AllowanceTableRowProps {
  workingHour: any;
  originalIndex: number;
  t: (key: string) => string;
  onEdit: (deductionData: any) => void;
  onRemove: () => void;
}

export const AllowanceTableRow: React.FC<AllowanceTableRowProps> = ({
  workingHour,
  originalIndex,
  t,
  onEdit,
  onRemove,
}) => {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  };

  const handleEdit = () => {
    // Map working hour data to deduction modal format
    const deductionData: any = {
      articleNumber: workingHour.articleNumber,
      deductionType: '', // Will be determined from articleNumber in modal
      files: [], // Files will be handled separately
      fileContent: workingHour.fileContent || '', // Add fileContent from API
      fileName: workingHour.fileName || '' // Add fileName from API
    };

    // Always populate common fields
    deductionData.isTaxFree = workingHour.isTaxFreeArticle ? 'yes' : 'no';
    deductionData.showOnInvoice = workingHour.isArticleShownInInvoice ? 'yes' : 'no';
    deductionData.showOnInvoiceFile = workingHour.isPdfAttachedToInvoice ? 'yes' : 'no';
    
    // IMPORTANT: Always populate VAT from backend for ALL deduction types
    
    if (workingHour.vat !== undefined && workingHour.vat !== null) {
      deductionData.vat = workingHour.vat?.toString() || '';
    }
    
    // Mileage Report fields (Article 93)
    if (workingHour.distance || workingHour.tripStartedPlace || workingHour.carRegistrationNo) {
      deductionData.travelDate = workingHour.workingDate || '';
      deductionData.distance = workingHour.distance?.toString() || workingHour.number?.toString() || '';
      deductionData.reason = workingHour.reasonForTrip || workingHour.workingDescription || '';
      deductionData.carRegistration = workingHour.carRegistrationNo || '';
      deductionData.tripStartPlace = workingHour.tripStartedPlace || '';
      deductionData.tripEndPlace = workingHour.tripEndedPlace || '';
      deductionData.total = workingHour.amount?.toString() || workingHour.amountInSek?.toString() || '0';
    }
    
    // Travel/Material expenses with amount fields
    if (workingHour.amountPerUnit || workingHour.amountExVat) {
      deductionData.amountExVat = workingHour.amount?.toString() || workingHour.amountInSek?.toString() || '';
      deductionData.travelDate = workingHour.workingDate || '';
      deductionData.dateOfPurchase = workingHour.workingDate || '';
    }
    
    // Allowance fields
    if (workingHour.tripStartedDate || workingHour.tripEndedDate || workingHour.allowancePerDay) {
      deductionData.tripStartDate = workingHour.tripStartedDate || workingHour.workingDate || '';
      deductionData.tripEndDate = workingHour.tripEndedDate || workingHour.workingDate || '';
      deductionData.reason = workingHour.reasonForTrip || workingHour.workingDescription || '';
      deductionData.country = workingHour.country || '';
      deductionData.destination = workingHour.allowanceDestination || '';
      deductionData.allowancePerDay = workingHour.allowancePerDay?.toString() || '';
      deductionData.isPartialWorkday = ''; // Not in API data, will default
    }

    onEdit(deductionData);
  };

  return (
    <div className="table-row allowance-table-row" key={`wh-${originalIndex}`}>
      <div className="col-drag d-none d-md-flex">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="9" cy="12" r="1" fill="#a4a7ae" />
          <circle cx="9" cy="5" r="1" fill="#a4a7ae" />
          <circle cx="9" cy="19" r="1" fill="#a4a7ae" />
          <circle cx="15" cy="12" r="1" fill="#a4a7ae" />
          <circle cx="15" cy="5" r="1" fill="#a4a7ae" />
          <circle cx="15" cy="19" r="1" fill="#a4a7ae" />
        </svg>
      </div>

      <div className="col-dates-allowance" data-label={t('DATE_S')}>
        <span className="date-value">{formatDate(workingHour.workingDate)}</span>
      </div>

      <div className="col-allowance-type" data-label={t('ALLOWANCE_TYPE')}>
        <div className="input-box">
          <span className="input-value">
            {workingHour.houseworkType || workingHour.workingDescription || `Article ${workingHour.articleNumber}`}
          </span>
        </div>
      </div>

      <div className="col-attachment" data-label={t('ATTACHMENT')}>
        <div className="attachment-box">
          <span className="file-name">{t('NO_FILE')}</span>
        </div>
      </div>

      <div className="col-price-allowance" data-label={t('PRICE')}>
        <div className="input-box">
          <span className="input-value">{workingHour.amount || 0}</span>
          <span className="currency-symbol">SEK</span>
        </div>
      </div>

      <div className="col-actions-allowance" data-label={t('ACTIONS')}>
        <Button variant="link" className="edit-btn" onClick={handleEdit}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path 
              d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" 
              stroke="#317AF7" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
            />
          </svg>
        </Button>
        <Button variant="link" className="remove-item-btn" onClick={onRemove}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path 
              d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" 
              stroke="#EF4444" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
            />
          </svg>
        </Button>
      </div>
    </div>
  );
};
